/**
 * Tier 1 · Themes — curated Brand starting points. The same five vibes the old
 * presets named, now first-class `BrandInputs` the designer applies as a start
 * and then refines in the Brand tab. These are _living starts_, not locked-in
 * presets: applying one just seeds `brand`, leaving every Brand control free.
 */
import type { BrandInputs } from "./brand.js";

export interface ThemePreset {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly brand: BrandInputs;
}

export const CURATED_THEMES: readonly ThemePreset[] = [
  {
    id: "minimal",
    name: "Minimal",
    description: "Calm and restrained, with generous spacing.",
    brand: {
      brandColor: "#111827",
      accentColor: "#6b7280",
      headingFont: "Inter",
      bodyFont: "Inter",
      cornerRoundness: "subtle",
      density: "spacious",
    },
  },
  {
    id: "bold",
    name: "Bold",
    description: "High-energy and warm, with rounded corners.",
    brand: {
      brandColor: "#dc2626",
      accentColor: "#f59e0b",
      headingFont: "Poppins",
      bodyFont: "Inter",
      cornerRoundness: "rounded",
      density: "comfortable",
    },
  },
  {
    id: "editorial",
    name: "Editorial",
    description: "Classic serif, print-like and square.",
    brand: {
      brandColor: "#1f2937",
      accentColor: "#b45309",
      headingFont: "Playfair Display",
      bodyFont: "Lora",
      cornerRoundness: "none",
      density: "spacious",
    },
  },
  {
    id: "creative",
    name: "Creative",
    description: "Playful and vivid, with pill shapes.",
    brand: {
      brandColor: "#7c3aed",
      accentColor: "#ec4899",
      headingFont: "Poppins",
      bodyFont: "Nunito",
      cornerRoundness: "pill",
      density: "comfortable",
    },
  },
  {
    id: "corporate",
    name: "Corporate",
    description: "Trustworthy blue, tidy and conventional.",
    brand: {
      brandColor: "#1d4ed8",
      accentColor: "#0ea5e9",
      headingFont: "Inter",
      bodyFont: "Inter",
      cornerRoundness: "rounded",
      density: "comfortable",
    },
  },
];

export function getTheme(id: string): ThemePreset | undefined {
  return CURATED_THEMES.find((t) => t.id === id);
}
